/**
 * useIntakeAssessment.ts — Issue #325.
 *
 * Admin-side hooks for golden analysis / deal intention / health assessment.
 *
 * 红线：
 *   - 只使用 generated @jinzhuan/api-client；不手写 fetch。
 *   - 所有 mutation 必须带 expectedVersion 走乐观锁。
 *   - 错误通过 ApiError 抛回 UI；不静默吞掉 HTTP 状态。
 *   - 不在前端伪造已审核/已批准状态；mutation 必须使用后端返回值刷新状态。
 *   - 列表视图只展示 compact summary 字段，详情视图才取整张 payload。
 */
import { useCallback, useState } from 'react';
import {
  createApiClient,
  ApiError as ApiClientError,
} from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

// ============================================================================
// Type aliases
// ============================================================================

export type IntakeAssessmentRecordSummaryDto =
  ApiComponents['schemas']['IntakeAssessmentRecordSummaryDto'];
export type IntakeAssessmentRecordDto =
  ApiComponents['schemas']['IntakeAssessmentRecordDto'];
export type IntakeAssessmentReviewDto =
  ApiComponents['schemas']['IntakeAssessmentReviewDto'];
export type CustomerIntakeAssessmentSummaryDto =
  ApiComponents['schemas']['CustomerIntakeAssessmentSummaryDto'];
export type CreateIntakeAssessmentRecordDto =
  ApiComponents['schemas']['CreateIntakeAssessmentRecordDto'];
export type UpdateIntakeAssessmentRecordDto =
  ApiComponents['schemas']['UpdateIntakeAssessmentRecordDto'];
export type ApproveIntakeAssessmentRecordDto =
  ApiComponents['schemas']['ApproveIntakeAssessmentRecordDto'];
export type RequestChangesIntakeAssessmentRecordDto =
  ApiComponents['schemas']['RequestChangesIntakeAssessmentRecordDto'];
export type CancelIntakeAssessmentRecordDto =
  ApiComponents['schemas']['CancelIntakeAssessmentRecordDto'];
export type SubmitIntakeAssessmentRecordDto =
  ApiComponents['schemas']['SubmitIntakeAssessmentRecordDto'];

export type IntakeAssessmentRecordKind =
  IntakeAssessmentRecordSummaryDto['recordKind'];
export type IntakeAssessmentStatus =
  IntakeAssessmentRecordSummaryDto['status'];

export const INTAKE_ASSESSMENT_RECORD_KINDS: IntakeAssessmentRecordKind[] = [
  'golden_analysis',
  'deal_intention',
  'health_assessment',
];

export const INTAKE_ASSESSMENT_KIND_LABELS: Record<
  IntakeAssessmentRecordKind,
  string
> = {
  golden_analysis: '黄金分析表',
  deal_intention: '成交意向表',
  health_assessment: '接诊评估',
};

export const INTAKE_ASSESSMENT_STATUS_LABELS: Record<
  IntakeAssessmentStatus,
  string
> = {
  draft: '草稿',
  submitted: '审核中',
  approved: '已批准',
  changes_requested: '待修改',
  cancelled: '已取消',
};

export const INTAKE_ASSESSMENT_STATUSES: IntakeAssessmentStatus[] = [
  'draft',
  'submitted',
  'approved',
  'changes_requested',
  'cancelled',
];

// ============================================================================
// Error helpers
// ============================================================================

export interface ApiError {
  message: string;
  code?: string;
  status?: number;
}

function toApiError(err: unknown, httpStatus?: number): ApiError {
  const status =
    httpStatus ?? (err instanceof ApiClientError ? err.status : undefined);
  if (status === 401) {
    return {
      message: '会话已失效，请重新登录后再试',
      code: 'UNAUTHENTICATED',
      status,
    };
  }
  if (status === 403) {
    return {
      message: '当前角色没有接诊评估模块访问权限',
      code: 'FORBIDDEN',
      status,
    };
  }
  if (status === 404) {
    return {
      message: '记录不存在或已被删除',
      code: 'NOT_FOUND',
      status,
    };
  }
  if (status === 409) {
    return {
      message: '记录已被其他操作修改或状态不允许该操作，请刷新后重试',
      code: 'CONFLICT',
      status,
    };
  }
  if (status === 400) {
    return {
      message: '请求被拒绝，请检查必填字段与字段长度',
      code: 'BAD_REQUEST',
      status,
    };
  }
  if (err instanceof Error) {
    return { message: err.message, status };
  }
  return { message: String(err), status };
}

// ============================================================================
// List
// ============================================================================

export interface ListFilter {
  recordKind?: IntakeAssessmentRecordKind;
  status?: IntakeAssessmentStatus;
  customerId?: string;
  ownerUserId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export function useIntakeAssessmentList() {
  const [rows, setRows] = useState<IntakeAssessmentRecordSummaryDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchRecords = useCallback(async (filter?: ListFilter) => {
    setLoading(true);
    setError(null);
    try {
      const query: Record<string, string | number> = {};
      if (filter?.recordKind) query.recordKind = filter.recordKind;
      if (filter?.status) query.status = filter.status;
      if (filter?.customerId) query.customerId = filter.customerId;
      if (filter?.ownerUserId) query.ownerUserId = filter.ownerUserId;
      if (filter?.search) query.search = filter.search;
      if (filter?.page) query.page = filter.page;
      if (filter?.limit) query.limit = filter.limit;

      const {
        data,
        error: apiErr,
        response,
      } = await apiClient.GET('/api/v1/intake-assessment/records', {
        params: { query },
      });
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      if (data) {
        setRows([...data.rows]);
        setTotal(data.total);
        setPage(data.page);
        setLimit(data.limit);
      }
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { rows, total, page, limit, loading, error, fetchRecords };
}

// ============================================================================
// Detail
// ============================================================================

export function useIntakeAssessmentDetail() {
  const [record, setRecord] = useState<IntakeAssessmentRecordDto | null>(null);
  const [reviews, setReviews] = useState<IntakeAssessmentReviewDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchRecord = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const {
        data: detailData,
        error: detailErr,
        response: detailResponse,
      } = await apiClient.GET('/api/v1/intake-assessment/records/{id}', {
        params: { path: { id } },
      });
      if (detailErr) {
        setError(toApiError(detailErr, (detailResponse as Response)?.status));
        return;
      }
      setRecord(detailData ?? null);

      const {
        data: reviewsData,
        error: reviewsErr,
        response: reviewsResponse,
      } = await apiClient.GET('/api/v1/intake-assessment/records/{id}/reviews', {
        params: { path: { id } },
      });
      if (reviewsErr) {
        setError(toApiError(reviewsErr, (reviewsResponse as Response)?.status));
        return;
      }
      setReviews([...(reviewsData ?? [])]);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { record, reviews, loading, error, fetchRecord, setRecord };
}

// ============================================================================
// Mutations
// ============================================================================

export function useCreateIntakeAssessmentRecord() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const createRecord = useCallback(
    async (
      dto: CreateIntakeAssessmentRecordDto,
    ): Promise<IntakeAssessmentRecordDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/intake-assessment/records',
          { body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { createRecord, loading, error };
}

export function useUpdateIntakeAssessmentRecord() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const updateRecord = useCallback(
    async (
      id: string,
      dto: UpdateIntakeAssessmentRecordDto,
    ): Promise<IntakeAssessmentRecordDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.PUT(
          '/api/v1/intake-assessment/records/{id}',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { updateRecord, loading, error };
}

export function useIntakeAssessmentTransitions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const submit = useCallback(
    async (
      id: string,
      dto: SubmitIntakeAssessmentRecordDto,
    ): Promise<IntakeAssessmentRecordDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/intake-assessment/records/{id}/submit',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const approve = useCallback(
    async (
      id: string,
      dto: ApproveIntakeAssessmentRecordDto,
    ): Promise<IntakeAssessmentRecordDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/intake-assessment/records/{id}/approve',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const requestChanges = useCallback(
    async (
      id: string,
      dto: RequestChangesIntakeAssessmentRecordDto,
    ): Promise<IntakeAssessmentRecordDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/intake-assessment/records/{id}/request-changes',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const cancel = useCallback(
    async (
      id: string,
      dto: CancelIntakeAssessmentRecordDto,
    ): Promise<IntakeAssessmentRecordDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/intake-assessment/records/{id}/cancel',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { submit, approve, requestChanges, cancel, loading, error };
}

// ============================================================================
// Approved summary for proposal
// ============================================================================

export function useIntakeAssessmentApprovedSummary() {
  const [summary, setSummary] =
    useState<CustomerIntakeAssessmentSummaryDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchSummary = useCallback(async (customerId: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/intake-assessment/customers/{customerId}/approved-summary',
        { params: { path: { customerId } } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      setSummary(data ?? null);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return { summary, loading, error, fetchSummary, setSummary };
}
